import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowSquareOut, Tag, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

function priceLabel(p) {
    if (p == null) return "";
    const n = Number(p);
    if (!n) return "Gratuit";
    return Number.isInteger(n) ? `${n} €` : `${n.toFixed(2).replace(".", ",")} €`;
}

export default function Offres() {
    const { data: offers = [], isLoading } = useQuery({ queryKey: ["offers"], queryFn: async () => (await api.get("/offers")).data });
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const [pub, setPub] = useState("Tous");

    const publics = useMemo(() => ["Tous", ...Array.from(new Set(offers.map((o) => o.public).filter((p) => p && p !== "Tous")))], [offers]);
    const filtered = pub === "Tous" ? offers : offers.filter((o) => o.public === pub);
    const offersUrl = settings?.tenup_offers_url || "https://tenup.fft.fr/club/61440326/offres";

    return (
        <div data-testid="offres-page">
            <PageHeader
                testid="offres-header"
                overline="Saison 2026-2027"
                title="Offre du club"
                subtitle="Adhésions, cours enfants, adultes et pickleball. Choisissez la formule qui vous convient et adhérez en ligne sur Ten'Up."
            />

            <div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex flex-wrap gap-2" data-testid="offres-filters">
                        {publics.map((p) => (
                            <button
                                key={p}
                                onClick={() => setPub(p)}
                                data-testid={`offres-filter-${p}`}
                                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                                    pub === p ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <a
                        href={offersUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="offres-tenup-all"
                        className="inline-flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-sm font-bold text-white hover:bg-clay-dark transition-colors"
                    >
                        Toutes les offres sur Ten'Up <ArrowSquareOut weight="bold" size={16} />
                    </a>
                </div>

                {isLoading ? (
                    <p className="text-slate-500 font-mono text-sm">Chargement…</p>
                ) : filtered.length === 0 ? (
                    <p className="text-slate-500" data-testid="offres-empty">Aucune offre disponible pour le moment.</p>
                ) : (
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((o, i) => (
                            <motion.div
                                key={o.id}
                                data-testid="offer-card"
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                className="group flex flex-col brand-card p-6 transition-colors duration-200 hover:border-clay"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 text-navy text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                                        <Tag weight="fill" size={12} /> {o.public}
                                    </span>
                                </div>
                                <h3 className="font-display font-extrabold text-xl text-navy leading-snug">{o.title}</h3>
                                <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{o.description}</p>
                                <div className="mt-5 flex items-end gap-1">
                                    <span className="font-display font-black text-3xl text-clay tabular-nums">{priceLabel(o.price)}</span>
                                    <span className="text-xs text-slate-400 mb-1.5">/ saison</span>
                                </div>
                                {o.tenup_url ? (
                                    <a
                                        href={o.tenup_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="offer-tenup-btn"
                                        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-navy-deep"
                                    >
                                        Choisir sur Ten'Up <ArrowSquareOut weight="bold" size={15} />
                                    </a>
                                ) : (
                                    <div className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-500">
                                        <Check weight="bold" size={15} /> Renseignements au club
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
